"use client";

import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import Input from "../ui/input";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAssessmentStore } from "@/app/(withNavbarOnly)/care-assessment/v1/assessment-store";
import { assessmentFormInputStyles } from "@/app/(withNavbarOnly)/care-assessment/v1/assessment-dynamic";

const schema = z.object({
  role: z.string().min(1, { message: "Role is required" }).max(32),
  organization: z
    .string()
    .min(1, { message: "Organization is required" })
    .max(32),
});

interface RoleFormInputs {
  role: string;
  organization: string;
}

const RoleForm = () => {
  const role = useAssessmentStore((state) => state.role);
  const organization = useAssessmentStore((state) => state.organization);
  const changeRole = useAssessmentStore((state) => state.changeRole);
  const changeOrganization = useAssessmentStore(
    (state) => state.changeOrganization
  );
  const makeRoleStepValid = useAssessmentStore(
    (state) => state.makeRoleStepValid
  );
  const makeRoleStepInvalid = useAssessmentStore(
    (state) => state.makeRoleStepInvalid
  );

  const {
    register,
    watch,
    formState: { isValid, errors },
  } = useForm<RoleFormInputs>({
    mode: "all",
    resolver: zodResolver(schema),
    defaultValues: {
      role,
      organization,
    },
  });

  // We do this because the submit button is at the parent component
  // (src/app/assessment/page.tsx) and they communicate using zustand state
  const roleValue = watch("role");
  const organizationValue = watch("organization");
  useEffect(() => {
    changeRole(roleValue);
  }, [changeRole, roleValue]);
  useEffect(() => {
    changeOrganization(organizationValue);
  }, [changeOrganization, organizationValue]);
  useEffect(() => {
    if (!isValid) {
      makeRoleStepInvalid();
      return;
    }
    makeRoleStepValid();
    return;
  }, [isValid, makeRoleStepInvalid, makeRoleStepValid]);

  return (
    <div
      aria-label="role form"
      className="flex max-w-[358px] flex-1 flex-col gap-4 sm:max-w-[458px] lg:max-w-[520px]"
    >
      <Input
        aria-label="Role"
        label="Role"
        placeholder="Role"
        error={!!errors.role}
        errorMessage={errors.role?.message}
        className={assessmentFormInputStyles}
        {...register("role")}
      />
      <Input
        aria-label="Organization"
        label="Organization"
        placeholder="Organization"
        error={!!errors.organization}
        errorMessage={errors.organization?.message}
        className={assessmentFormInputStyles}
        {...register("organization")}
      />
    </div>
  );
};

export default RoleForm;
